/**
 * MB Personalization - Clean Architecture with Selective Section Updates
 *
 * Server-side: Pre-filters segments (active + timing) with regex patterns.
 * Client-side: URL regex matching + cart sync + selective section updates.
 */

// -----------------------------------------------------------------------------
// Runtime config
// -----------------------------------------------------------------------------

const MB_PERSONALIZATION_RUNTIME_CONFIG = Object.freeze({
  pendingClass: 'mb-personalization-pending',
  readyClass: 'mb-personalization-ready',
  highReadyClass: 'mb-personalization-high-ready',
  failsafeTimeoutMs: 8000,
  defaultBatchSize: 5,
  segmentSessionTimeoutMinutes: 360,
  cartSyncVerifyRetries: 8,
  cartSyncVerifyDelayMs: 250,
  cartWriteCooldownMs: 120000,
  cartWriteCooldownStorageKey: 'mb_cart_write_cooldown_until',
  debugBufferEnabledDefault: false,
  debugBufferKey: 'mb_personalization_debug_buffer',
  debugBufferLimit: 150,
  suppressVerboseDebugEvents: true,
  sectionStateDiagnosticsEnabled: false,
  cartVerificationMode: 'strict',
  postPipelineDriftChecks: true,
  postPipelineDriftConfirmationDelayMs: 180,
  sectionFetchCacheStrategy: 'aggressive',
  runSummaryEnabled: true,
  runSummaryConsoleEnabled: true,
  runSummaryLimit: 30,
  runSummaryStorageKey: 'mb_personalization_run_summaries'
});

const MB_PERSONALIZATION_PENDING_CLASS = MB_PERSONALIZATION_RUNTIME_CONFIG.pendingClass;
const MB_PERSONALIZATION_READY_CLASS = MB_PERSONALIZATION_RUNTIME_CONFIG.readyClass;
const MB_PERSONALIZATION_HIGH_READY_CLASS = MB_PERSONALIZATION_RUNTIME_CONFIG.highReadyClass;
const MB_PERSONALIZATION_FAILSAFE_TIMEOUT_MS = MB_PERSONALIZATION_RUNTIME_CONFIG.failsafeTimeoutMs;
const MB_DEFAULT_BATCH_SIZE = MB_PERSONALIZATION_RUNTIME_CONFIG.defaultBatchSize;
const MB_SEGMENT_SESSION_TIMEOUT_MINUTES = MB_PERSONALIZATION_RUNTIME_CONFIG.segmentSessionTimeoutMinutes;
const MB_CART_SYNC_VERIFY_RETRIES = MB_PERSONALIZATION_RUNTIME_CONFIG.cartSyncVerifyRetries;
const MB_CART_SYNC_VERIFY_DELAY_MS = MB_PERSONALIZATION_RUNTIME_CONFIG.cartSyncVerifyDelayMs;
const MB_CART_WRITE_COOLDOWN_MS = MB_PERSONALIZATION_RUNTIME_CONFIG.cartWriteCooldownMs;
const MB_CART_WRITE_COOLDOWN_STORAGE_KEY = MB_PERSONALIZATION_RUNTIME_CONFIG.cartWriteCooldownStorageKey;
const MB_DEBUG_BUFFER_ENABLED_DEFAULT = MB_PERSONALIZATION_RUNTIME_CONFIG.debugBufferEnabledDefault;
const MB_DEBUG_BUFFER_KEY = MB_PERSONALIZATION_RUNTIME_CONFIG.debugBufferKey;
const MB_DEBUG_BUFFER_LIMIT = MB_PERSONALIZATION_RUNTIME_CONFIG.debugBufferLimit;
const MB_RUN_SUMMARY_STORAGE_KEY = MB_PERSONALIZATION_RUNTIME_CONFIG.runSummaryStorageKey;
const MB_DEBUG_QUERY_OVERRIDE = (() => {
  try {
    const debugValue = new URLSearchParams(window.location.search).get('debug');
    if (debugValue === null) return null;

    const normalized = String(debugValue).trim().toLowerCase();
    return normalized === 'true' || normalized === '1' || normalized === 'yes' || normalized === 'on';
  } catch (error) {
    return null;
  }
})();
const MB_DEBUG_CONSOLE_STREAM = MB_DEBUG_QUERY_OVERRIDE === true;

let mbCurrentTrackerSessionId = null;
let mbCurrentRunSummary = null;

const MB_RUN_SCORE_THRESHOLDS = Object.freeze({
  cartNetwork: { greenMax: 1200, yellowMax: 1800 },
  responseParse: { greenMax: 300, yellowMax: 800 },
  sectionFetchTotal: { greenMax: 1800, yellowMax: 2800 }
});

function toFiniteNumber(value) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function getMetricBand(value, thresholds) {
  if (!Number.isFinite(value)) return 'na';
  if (value <= thresholds.greenMax) return 'green';
  if (value <= thresholds.yellowMax) return 'yellow';
  return 'red';
}

function scoreRunSummary(summary = {}) {
  const requestNetworkMs = toFiniteNumber(summary.requestNetworkMs);
  const responseParseMs = toFiniteNumber(summary.responseParseMs);
  const highPriorityFetchMs = toFiniteNumber(summary.highPriorityFetchMs);
  const normalPriorityFetchMs = toFiniteNumber(summary.normalPriorityFetchMs);
  const sectionFetchTotalMs = Number.isFinite(highPriorityFetchMs) || Number.isFinite(normalPriorityFetchMs)
    ? Number(((highPriorityFetchMs || 0) + (normalPriorityFetchMs || 0)).toFixed(1))
    : null;

  const metrics = {
    requestNetworkMs: {
      value: requestNetworkMs,
      band: getMetricBand(requestNetworkMs, MB_RUN_SCORE_THRESHOLDS.cartNetwork)
    },
    responseParseMs: {
      value: responseParseMs,
      band: getMetricBand(responseParseMs, MB_RUN_SCORE_THRESHOLDS.responseParse)
    },
    sectionFetchTotalMs: {
      value: sectionFetchTotalMs,
      band: getMetricBand(sectionFetchTotalMs, MB_RUN_SCORE_THRESHOLDS.sectionFetchTotal)
    }
  };

  const bands = Object.values(metrics).map(metric => metric.band);
  const redCount = bands.filter(band => band === 'red').length;
  const yellowCount = bands.filter(band => band === 'yellow').length;

  let classification = 'Healthy';
  if (redCount >= 2) {
    classification = 'Mixed degradation';
  } else if (metrics.requestNetworkMs.band === 'red' && metrics.responseParseMs.band !== 'red') {
    classification = 'Network-bad';
  } else if (metrics.responseParseMs.band === 'red' && metrics.requestNetworkMs.band !== 'red') {
    classification = 'Client-bad';
  } else if (redCount === 1 || yellowCount > 1) {
    classification = 'Watch';
  }

  return {
    classification,
    redCount,
    yellowCount,
    thresholds: MB_RUN_SCORE_THRESHOLDS,
    metrics
  };
}

function initRunSummary() {
  mbCurrentRunSummary = {
    tsIso: new Date().toISOString(),
    path: window.location.pathname + window.location.search,
    pipelineStartPerfMs: Number(performance.now().toFixed(1)),
    trackerSessionId: mbCurrentTrackerSessionId || ''
  };
}

function updateRunSummary(patch = {}) {
  if (!mbCurrentRunSummary || !getRuntimeOption('runSummaryEnabled')) return;
  Object.assign(mbCurrentRunSummary, patch);
}

function readRunSummaries() {
  try {
    const raw = sessionStorage.getItem(MB_RUN_SUMMARY_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch (error) {
    return [];
  }
}

function writeRunSummaries(entries) {
  try {
    sessionStorage.setItem(MB_RUN_SUMMARY_STORAGE_KEY, JSON.stringify(entries));
  } catch (error) {
    // Ignore storage write failures in constrained browser modes.
  }
}

function finalizeRunSummary(status) {
  if (!mbCurrentRunSummary || !getRuntimeOption('runSummaryEnabled')) return;

  const nowPerf = Number(performance.now().toFixed(1));
  const startPerf = Number(mbCurrentRunSummary.pipelineStartPerfMs || nowPerf);
  const revealPerf = Number(mbCurrentRunSummary.highPriorityRevealPerfMs || 0);
  const cartStartPerf = Number(mbCurrentRunSummary.cartUpdateStartPerfMs || 0);
  const cartEndPerf = Number(mbCurrentRunSummary.cartUpdateEndPerfMs || 0);

  const summary = {
    ...mbCurrentRunSummary,
    status,
    pipelineEndPerfMs: nowPerf,
    totalMsFromPipelineStart: Number((nowPerf - startPerf).toFixed(1)),
    highPriorityRevealMsFromPipelineStart: revealPerf > 0
      ? Number((revealPerf - startPerf).toFixed(1))
      : null,
    highPriorityRevealMsFromCartStart: revealPerf > 0 && cartStartPerf > 0
      ? Number((revealPerf - cartStartPerf).toFixed(1))
      : null,
    highPrioritySectionWorkMs: revealPerf > 0 && cartEndPerf > 0
      ? Number((revealPerf - cartEndPerf).toFixed(1))
      : null
  };

  summary.runScore = scoreRunSummary(summary);

  const existing = readRunSummaries();
  const limit = Math.max(1, Number(getRuntimeOption('runSummaryLimit')) || 30);
  existing.push(summary);
  writeRunSummaries(existing.slice(-limit));

  appendDebugBuffer('run_summary', summary);

  if (getRuntimeOption('runSummaryConsoleEnabled')) {
    console.log('[MB Personalization Run Summary]', summary);
  }

  mbCurrentRunSummary = null;
}

window.MB_READ_RUN_SUMMARIES = function MB_READ_RUN_SUMMARIES() {
  return readRunSummaries();
};

// -----------------------------------------------------------------------------
// Runtime coordination
// -----------------------------------------------------------------------------

let mbPersonalizationCartSyncResolver = null;
if (!window.MB_PERSONALIZATION_CART_SYNC_PROMISE || typeof window.MB_PERSONALIZATION_CART_SYNC_RESOLVE !== 'function') {
  window.MB_PERSONALIZATION_CART_SYNC_PROMISE = new Promise(resolve => {
    mbPersonalizationCartSyncResolver = resolve;
    window.MB_PERSONALIZATION_CART_SYNC_RESOLVE = resolve;
  });
} else {
  mbPersonalizationCartSyncResolver = window.MB_PERSONALIZATION_CART_SYNC_RESOLVE;
}

// -----------------------------------------------------------------------------
// Debug instrumentation
// -----------------------------------------------------------------------------

function getRuntimeOverrides() {
  const overrides = window.MB_PERSONALIZATION_RUNTIME_OVERRIDES;
  return overrides && typeof overrides === 'object' ? overrides : null;
}

function getRuntimeOption(key) {
  const overrides = getRuntimeOverrides();
  if (overrides && Object.prototype.hasOwnProperty.call(overrides, key)) {
    return overrides[key];
  }

  return MB_PERSONALIZATION_RUNTIME_CONFIG[key];
}

function isDebugBufferEnabled() {
  if (typeof window.MB_PERSONALIZATION_DEBUG === 'boolean') {
    return window.MB_PERSONALIZATION_DEBUG;
  }

  if (typeof MB_DEBUG_QUERY_OVERRIDE === 'boolean') {
    return MB_DEBUG_QUERY_OVERRIDE;
  }

  return Boolean(getRuntimeOption('debugBufferEnabledDefault'));
}

function resolveCartVerificationSettings(options = {}) {
  const mode = getRuntimeOption('cartVerificationMode');

  if (mode === 'off') {
    return {
      enabled: false,
      verifyRetries: 0,
      verifyDelayMs: 0
    };
  }

  if (mode === 'light') {
    return {
      enabled: true,
      verifyRetries: Math.min(2, options.verifyRetries ?? MB_CART_SYNC_VERIFY_RETRIES),
      verifyDelayMs: options.verifyDelayMs ?? MB_CART_SYNC_VERIFY_DELAY_MS
    };
  }

  return {
    enabled: true,
    verifyRetries: options.verifyRetries ?? MB_CART_SYNC_VERIFY_RETRIES,
    verifyDelayMs: options.verifyDelayMs ?? MB_CART_SYNC_VERIFY_DELAY_MS
  };
}

function buildSectionFetchRequest(baseUrl, separator, sectionsParam, cacheBust) {
  const strategy = getRuntimeOption('sectionFetchCacheStrategy');
  const includeCacheBust = strategy === 'aggressive' || strategy === 'query-param-only';
  const useNoStore = strategy === 'aggressive' || strategy === 'fetch-no-store-only';
  const useNoCacheHeader = strategy === 'aggressive';
  let url = includeCacheBust
    ? `${baseUrl}${separator}sections=${sectionsParam}&_mbps=${encodeURIComponent(cacheBust)}`
    : `${baseUrl}${separator}sections=${sectionsParam}`;

  const headers = {
    'X-Requested-With': 'XMLHttpRequest'
  };

  if (useNoCacheHeader) {
    headers['Cache-Control'] = 'no-cache';
  }

  const requestOptions = { headers };
  if (useNoStore) {
    requestOptions.cache = 'no-store';
  }

  return { url, requestOptions };
}

function readDebugBuffer() {
  try {
    const raw = sessionStorage.getItem(MB_DEBUG_BUFFER_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch (error) {
    return [];
  }
}

function writeDebugBuffer(entries) {
  try {
    sessionStorage.setItem(MB_DEBUG_BUFFER_KEY, JSON.stringify(entries));
  } catch (error) {
    // Ignore storage write failures in constrained browser modes.
  }
}

function logDebugBufferSnapshot() {
  if (!MB_DEBUG_CONSOLE_STREAM) return;

  try {
    console.log('[MB Personalization Debug Buffer Snapshot]', JSON.stringify(readDebugBuffer()));
  } catch (error) {
    console.warn('Failed to stringify debug buffer snapshot:', error);
  }
}

const MB_VERBOSE_DEBUG_EVENTS = new Set([
  'sections_fetch_mode',
  'sections_fetch_targets',
  'sections_update_batches_prepared',
  'section_fetch_request_built',
  'sections_batch_fetched',
  'sections_dom_state_diagnostics'
]);

function shouldLogDebugEvent(event) {
  const suppressVerbose = Boolean(getRuntimeOption('suppressVerboseDebugEvents'));
  if (!suppressVerbose) return true;

  return !MB_VERBOSE_DEBUG_EVENTS.has(event);
}

function appendDebugBuffer(event, details = {}) {
  if (!isDebugBufferEnabled()) return;
  if (!shouldLogDebugEvent(event)) return;

  const entry = {
    tsIso: new Date().toISOString(),
    perfMs: Number(performance.now().toFixed(1)),
    event,
    details
  };

  const existing = readDebugBuffer();
  existing.push(entry);
  const sliced = existing.slice(-MB_DEBUG_BUFFER_LIMIT);
  writeDebugBuffer(sliced);
}

window.MB_READ_DEBUG_BUFFER = function MB_READ_DEBUG_BUFFER() {
  return readDebugBuffer();
};

window.MB_CLEAR_DEBUG_BUFFER = function MB_CLEAR_DEBUG_BUFFER() {
  writeDebugBuffer([]);
};

function createPersonalizationTracker() {
  const trackerSessionId = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  mbCurrentTrackerSessionId = trackerSessionId;
  initRunSummary();

  appendDebugBuffer('tracker_initialized', {
    trackerSessionId,
    url: window.location.pathname + window.location.search
  });

  return {
    mark(eventName, details = {}) {
      appendDebugBuffer(eventName, {
        trackerSessionId,
        ...details
      });
    }
  };
}

// -----------------------------------------------------------------------------
// Reveal and DOM helpers
// -----------------------------------------------------------------------------

function revealPersonalizedContent(tracker, reason) {
  document.documentElement.classList.remove(MB_PERSONALIZATION_PENDING_CLASS);
  document.documentElement.classList.remove(MB_PERSONALIZATION_HIGH_READY_CLASS);
  document.documentElement.classList.add(MB_PERSONALIZATION_READY_CLASS);
  tracker?.mark('content_revealed', { reason });
}

function getPersonalizationClassState() {
  const root = document.documentElement;

  return {
    classes: root.className,
    pending: root.classList.contains(MB_PERSONALIZATION_PENDING_CLASS),
    highReady: root.classList.contains(MB_PERSONALIZATION_HIGH_READY_CLASS),
    ready: root.classList.contains(MB_PERSONALIZATION_READY_CLASS)
  };
}

function markPersonalizationClassState(tracker, eventName, details = {}) {
  tracker?.mark(eventName, {
    ...getPersonalizationClassState(),
    ...details
  });
}

function revealHighPriorityContent(tracker, reason) {
  document.documentElement.classList.add(MB_PERSONALIZATION_HIGH_READY_CLASS);
  updateRunSummary({
    highPriorityRevealPerfMs: Number(performance.now().toFixed(1)),
    highPriorityRevealReason: reason
  });
  tracker?.mark('high_priority_content_revealed', { reason });
  markPersonalizationClassState(tracker, 'high_priority_content_class_state', { reason });
}

function getSectionElement(sectionId) {
  return document.getElementById(`shopify-section-${sectionId}`);
}

function waitFor(ms) {
  return new Promise(resolve => {
    window.setTimeout(resolve, ms);
  });
}

function parseSegmentSet(segmentString) {
  if (!segmentString || segmentString === 'null') return [];

  return segmentString
    .split(',')
    .map(segment => segment.trim())
    .filter(Boolean)
    .sort((a, b) => a.localeCompare(b));
}

function areSegmentSetsEqual(leftSegments, rightSegments) {
  const left = parseSegmentSet(leftSegments);
  const right = parseSegmentSet(rightSegments);

  if (left.length !== right.length) return false;
  return left.every((segment, index) => segment === right[index]);
}

function normalizeSegmentToken(segment) {
  if (typeof segment !== 'string') return '';
  return segment.toLowerCase().replace(/[_\-\s]/g, '');
}

function normalizeQuotedAttributeValue(value) {
  if (typeof value !== 'string') return '';

  return value.trim().replace(/^['"]+|['"]+$/g, '');
}

function publishPersonalizationCartGuard(attributes) {
  const segment = attributes?.segment;
  if (typeof segment !== 'string' || segment.trim() === '') return;

  window.MB_PERSONALIZATION_ATTRIBUTE_GUARD = {
    segment,
    current_campaign_id: attributes?.current_campaign_id || '',
    lp_url: attributes?.lp_url || '',
    expiresAt: Date.now() + 15000
  };

  appendDebugBuffer('personalization_attribute_guard_published', {
    segment,
    hasCampaignId: Boolean(attributes?.current_campaign_id),
    hasLpUrl: Boolean(attributes?.lp_url)
  });
}

/**
 * Keep the client-readable `campaign_attributes` sessionStorage mirror in
 * lock-step with the cart source of truth (`cart.attributes.current_campaign_id`).
 *
 * This mirror is READ-ONLY for analytics (GTM listener + the separately managed
 * Shopify Customer Events pixel). Personalization stays the source of truth via
 * the cart attribute; this only reflects the resolved value so analytics can read
 * it without a cart fetch.
 *
 *  - Writes / restores the campaign id when one is active (first-hit, restore from
 *    cart on a returning visit, or an override).
 *  - Removes the stale campaign id when none is active (expired / cleared / the
 *    cart attribute no longer contains a campaign).
 *  - Preserves the sibling keys (`itc`, `lp_url`) written by other scripts.
 *
 * @param {string} authoritativeCampaignId - resolved campaign id ('' to clear).
 * @param {string} [lpUrlToSet] - optional lp_url to also persist (unchanged behavior).
 */
function syncCampaignSessionMirror(authoritativeCampaignId, lpUrlToSet) {
  try {
    const SESSION_KEY = 'campaign_attributes';
    let existing = {};
    try {
      existing = JSON.parse(sessionStorage.getItem(SESSION_KEY) || '{}');
    } catch (parseError) {
      existing = {};
    }
    if (!existing || typeof existing !== 'object') {
      existing = {};
    }

    // Faithful mirror of the source-of-truth value. Strict character-set /
    // 100-char enforcement is done by the analytics readers (GTM listener +
    // Customer Events), never here, so the mirror never diverges from the cart.
    let clean = (authoritativeCampaignId == null) ? '' : String(authoritativeCampaignId).trim();
    if (clean.length > 200) {
      clean = clean.slice(0, 200);
    }

    if (clean) {
      existing.current_campaign_id = clean;
    } else if (Object.prototype.hasOwnProperty.call(existing, 'current_campaign_id')) {
      delete existing.current_campaign_id;
    }

    if (typeof lpUrlToSet === 'string') {
      if (lpUrlToSet) {
        existing.lp_url = lpUrlToSet;
      } else {
        delete existing.lp_url;
      }
    }

    sessionStorage.setItem(SESSION_KEY, JSON.stringify(existing));
  } catch (mirrorError) {
    // Non-fatal: the mirror is analytics-only and must never block personalization.
  }
}
// -----------------------------------------------------------------------------
// Cart write protection and recovery
// -----------------------------------------------------------------------------

function getCartWriteCooldownRemainingMs() {
  const storageUntil = Number(localStorage.getItem(MB_CART_WRITE_COOLDOWN_STORAGE_KEY) || 0);
  const memoryUntil = Number(window.MB_CART_WRITE_COOLDOWN_UNTIL || 0);
  const until = Math.max(storageUntil, memoryUntil);

  if (storageUntil && storageUntil > memoryUntil) {
    window.MB_CART_WRITE_COOLDOWN_UNTIL = storageUntil;
  }

  if (!until) return 0;
  return Math.max(0, until - Date.now());
}

function setCartWriteCooldown(cooldownMs, reason) {
  const ms = Math.max(1000, Number(cooldownMs) || MB_CART_WRITE_COOLDOWN_MS);
  const untilTs = Date.now() + ms;
  window.MB_CART_WRITE_COOLDOWN_UNTIL = untilTs;
  try {
    localStorage.setItem(MB_CART_WRITE_COOLDOWN_STORAGE_KEY, String(untilTs));
  } catch (error) {
    // Ignore storage failures.
  }
  appendDebugBuffer('cart_write_cooldown_set', {
    cooldownMs: ms,
    reason
  });
}

function clearCartWriteCooldown() {
  window.MB_CART_WRITE_COOLDOWN_UNTIL = 0;
  try {
    localStorage.removeItem(MB_CART_WRITE_COOLDOWN_STORAGE_KEY);
  } catch (error) {
    // Ignore storage failures.
  }
}

function scheduleDeferredSegmentSyncRetry(attributes, tracker) {
  const expectedSegment = attributes?.segment || '';
  if (!expectedSegment) return;

  if (window.MB_DEFERRED_SEGMENT_RETRY_ACTIVE) return;

  const cooldownRemainingMs = Math.max(1000, getCartWriteCooldownRemainingMs());
  window.MB_DEFERRED_SEGMENT_RETRY_ACTIVE = true;

  appendDebugBuffer('deferred_segment_retry_scheduled', {
    cooldownRemainingMs,
    expectedSegment
  });
  tracker?.mark('deferred_segment_retry_scheduled', {
    cooldownRemainingMs,
    expectedSegment
  });

  window.setTimeout(async () => {
    appendDebugBuffer('deferred_segment_retry_started', {
      expectedSegment
    });

    const retryResult = await updateCartAttributesWithVerification(
      attributes,
      {
        expectedSegment,
        verifyRetries: MB_CART_SYNC_VERIFY_RETRIES,
        verifyDelayMs: MB_CART_SYNC_VERIFY_DELAY_MS,
        tracker,
        trackerLabel: 'deferred_segment_retry'
      }
    );

    if (retryResult.ok) {
      clearCartWriteCooldown();
      publishPersonalizationCartGuard(attributes);
      tracker?.mark('deferred_segment_retry_success', {
        expectedSegment
      });
      await updatePersonalizedSections(tracker);
      schedulePostPipelineSegmentDriftChecks(expectedSegment, tracker);
    } else {
      tracker?.mark('deferred_segment_retry_failed', {
        expectedSegment,
        rateLimited: Boolean(retryResult.rateLimited),
        message: String(retryResult.error)
      });
    }

    window.MB_DEFERRED_SEGMENT_RETRY_ACTIVE = false;
  }, cooldownRemainingMs + 200);
}

async function runWithCartAttributeLock(task, label) {
  const lock = window.MB_WITH_CART_ATTRIBUTE_LOCK;
  if (typeof lock === 'function') {
    return lock(task, label);
  }

  return task();
}

// -----------------------------------------------------------------------------
// Cart and verification helpers
// -----------------------------------------------------------------------------

async function fetchCartSnapshot() {
  try {
    const response = await fetch('/cart.js', {
      headers: {
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      }
    });

    if (!response.ok) return null;
    return await response.json();
  } catch (error) {
    return null;
  }
}

function schedulePostPipelineSegmentDriftChecks(expectedSegment, tracker) {
  if (!getRuntimeOption('postPipelineDriftChecks')) {
    return;
  }

  if (typeof expectedSegment !== 'string' || expectedSegment.trim() === '') {
    return;
  }

  const confirmationDelayMs = Math.max(0, Number(getRuntimeOption('postPipelineDriftConfirmationDelayMs')) || 0);

  const checkDelays = [800, 2400];

  checkDelays.forEach(delayMs => {
    window.setTimeout(async () => {
      const snapshot = await fetchCartSnapshot();
      const persistedSegment = snapshot?.attributes?.segment || '';

      if (areSegmentSetsEqual(expectedSegment, persistedSegment)) {
        return;
      }

      appendDebugBuffer('post_pipeline_segment_check', {
        delayMs,
        expectedSegment,
        persistedSegment
      });

      tracker?.mark('post_pipeline_segment_drift_detected', {
        delayMs,
        expectedSegment,
        persistedSegment
      });

      if (confirmationDelayMs > 0) {
        await waitFor(confirmationDelayMs);
      }

      const confirmSnapshot = await fetchCartSnapshot();
      const confirmedPersistedSegment = confirmSnapshot?.attributes?.segment || '';

      appendDebugBuffer('post_pipeline_segment_recheck', {
        delayMs,
        confirmationDelayMs,
        expectedSegment,
        persistedSegment: confirmedPersistedSegment
      });

      if (areSegmentSetsEqual(expectedSegment, confirmedPersistedSegment)) {
        tracker?.mark('post_pipeline_segment_drift_transient', {
          delayMs,
          confirmationDelayMs,
          expectedSegment,
          persistedSegment,
          confirmedPersistedSegment
        });
        return;
      }

      const recoveryResult = await updateCartAttributesWithVerification(
        { segment: expectedSegment },
        {
          expectedSegment,
          verifyRetries: 4,
          verifyDelayMs: 200,
          tracker,
          trackerLabel: `post_pipeline_recovery_${delayMs}`
        }
      );

      if (recoveryResult.ok) {
        tracker?.mark('post_pipeline_segment_recovered', {
          delayMs,
          expectedSegment
        });
      } else {
        if (recoveryResult.rateLimited) {
          tracker?.mark('post_pipeline_segment_recovery_rate_limited', {
            delayMs,
            expectedSegment,
            cooldownRemainingMs: recoveryResult.cooldownRemainingMs || 0
          });
          return;
        }
        tracker?.mark('post_pipeline_segment_recovery_failed', {
          delayMs,
          expectedSegment,
          message: String(recoveryResult.error)
        });
      }
    }, delayMs);
  });
}

async function updateCartAttributesWithVerification(attributes, options = {}) {
  const {
    expectedSegment,
    tracker,
    trackerLabel = 'cart_attributes_update'
  } = options;
  const verificationSettings = resolveCartVerificationSettings(options);
  const verifyRetries = verificationSettings.verifyRetries;
  const verifyDelayMs = verificationSettings.verifyDelayMs;

  const startedAt = performance.now();

  const cooldownRemainingMs = getCartWriteCooldownRemainingMs();
  if (cooldownRemainingMs > 0) {
    appendDebugBuffer('cart_update_skipped_cooldown', {
      trackerLabel,
      cooldownRemainingMs,
      attributes
    });
    tracker?.mark(`${trackerLabel}_skipped_cooldown`, {
      cooldownRemainingMs
    });
    return {
      ok: false,
      cart: null,
      rateLimited: true,
      cooldownRemainingMs,
      error: new Error(`Cart updates temporarily rate-limited for ${cooldownRemainingMs}ms`)
    };
  }

  appendDebugBuffer('cart_update_attempt', {
    trackerLabel,
    expectedSegment: typeof expectedSegment === 'string' ? expectedSegment : null,
    attributes
  });

  if (trackerLabel === 'cart_update') {
    updateRunSummary({
      cartUpdateStartPerfMs: Number(performance.now().toFixed(1))
    });
  }

  const lockQueuedAt = performance.now();

  try {
    return await runWithCartAttributeLock(async () => {
      const lockAcquiredAt = performance.now();
      const lockWaitMs = Number((lockAcquiredAt - lockQueuedAt).toFixed(1));
      if (trackerLabel === 'cart_update') {
        updateRunSummary({ lockWaitMs });
      }
      tracker?.mark(`${trackerLabel}_lock_acquired`, {
        lockWaitMs
      });

      const requestStartedAt = performance.now();
      const updateResponse = await fetch('/cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({ attributes })
      });
      const responseReceivedAt = performance.now();
      const requestNetworkMs = Number((responseReceivedAt - requestStartedAt).toFixed(1));
      if (trackerLabel === 'cart_update') {
        updateRunSummary({ requestNetworkMs });
      }
      tracker?.mark(`${trackerLabel}_network_complete`, {
        lockWaitMs,
        requestNetworkMs,
        status: updateResponse.status
      });

      if (!updateResponse.ok) {
        const errorText = await updateResponse.text();
        const isRateLimited = updateResponse.status === 429;
        if (isRateLimited) {
          const retryAfterHeader = Number(updateResponse.headers.get('Retry-After'));
          const retryAfterMs = Number.isFinite(retryAfterHeader) && retryAfterHeader > 0
            ? retryAfterHeader * 1000
            : MB_CART_WRITE_COOLDOWN_MS;
          setCartWriteCooldown(retryAfterMs, trackerLabel);
        }
        appendDebugBuffer('cart_update_http_failure', {
          trackerLabel,
          status: updateResponse.status,
          errorText,
          lockWaitMs,
          requestNetworkMs
        });
        tracker?.mark(`${trackerLabel}_failed`, {
          status: updateResponse.status,
          lockWaitMs,
          requestNetworkMs,
          durationMs: Number((performance.now() - startedAt).toFixed(1))
        });
        return {
          ok: false,
          cart: null,
          rateLimited: isRateLimited,
          status: updateResponse.status,
          cooldownRemainingMs: isRateLimited ? getCartWriteCooldownRemainingMs() : 0,
          error: new Error(`Cart update failed (${updateResponse.status}): ${errorText}`)
        };
      }

      const parseStartedAt = performance.now();
      const updatePayload = await updateResponse.json().catch(() => null);
      const responseParseMs = Number((performance.now() - parseStartedAt).toFixed(1));
      if (trackerLabel === 'cart_update') {
        updateRunSummary({ responseParseMs });
      }
      tracker?.mark(`${trackerLabel}_response_parsed`, {
        lockWaitMs,
        requestNetworkMs,
        responseParseMs
      });

      if (typeof expectedSegment !== 'string') {
        tracker?.mark(`${trackerLabel}_success`, {
          lockWaitMs,
          requestNetworkMs,
          responseParseMs,
          durationMs: Number((performance.now() - startedAt).toFixed(1))
        });
        return {
          ok: true,
          cart: updatePayload
        };
      }

      let verifiedCart = updatePayload;
      let persistedSegment = verifiedCart?.attributes?.segment ?? null;

      if (!verificationSettings.enabled) {
        tracker?.mark(`${trackerLabel}_success_unverified`, {
          lockWaitMs,
          requestNetworkMs,
          responseParseMs,
          durationMs: Number((performance.now() - startedAt).toFixed(1))
        });
        appendDebugBuffer('cart_update_success_unverified', {
          trackerLabel,
          expectedSegment: typeof expectedSegment === 'string' ? expectedSegment : null,
          persistedSegment,
          lockWaitMs,
          requestNetworkMs,
          responseParseMs
        });

        return {
          ok: true,
          cart: verifiedCart,
          verifiedAttempts: 0,
          unverified: true
        };
      }

      if (!areSegmentSetsEqual(expectedSegment, persistedSegment)) {
        for (let attempt = 1; attempt <= verifyRetries; attempt += 1) {
          await waitFor(verifyDelayMs);
          verifiedCart = await fetchCartSnapshot();
          persistedSegment = verifiedCart?.attributes?.segment ?? null;
          appendDebugBuffer('cart_update_verify_attempt', {
            trackerLabel,
            attempt,
            expectedSegment,
            persistedSegment
          });

          if (areSegmentSetsEqual(expectedSegment, persistedSegment)) {
            appendDebugBuffer('cart_update_verify_success', {
              trackerLabel,
              attempt,
              expectedSegment
            });
            tracker?.mark(`${trackerLabel}_verified`, {
              attempts: attempt,
              lockWaitMs,
              requestNetworkMs,
              responseParseMs,
              durationMs: Number((performance.now() - startedAt).toFixed(1))
            });
            return {
              ok: true,
              cart: verifiedCart,
              verifiedAttempts: attempt
            };
          }
        }

        tracker?.mark(`${trackerLabel}_verify_failed`, {
          expectedSegment,
          persistedSegment,
          lockWaitMs,
          requestNetworkMs,
          responseParseMs,
          durationMs: Number((performance.now() - startedAt).toFixed(1))
        });
        appendDebugBuffer('cart_update_verify_failed', {
          trackerLabel,
          expectedSegment,
          persistedSegment
        });

        return {
          ok: false,
          cart: verifiedCart,
          error: new Error('Cart segment verification failed')
        };
      }

      tracker?.mark(`${trackerLabel}_success`, {
        lockWaitMs,
        requestNetworkMs,
        responseParseMs,
        expectedSegment,
        persistedSegment,
        durationMs: Number((performance.now() - startedAt).toFixed(1))
      });
      if (trackerLabel === 'cart_update') {
        const completedAt = performance.now();
        updateRunSummary({
          cartUpdateDurationMs: Number((completedAt - startedAt).toFixed(1)),
          cartUpdateEndPerfMs: Number(completedAt.toFixed(1))
        });
      }

      return {
        ok: true,
        cart: verifiedCart,
        verifiedAttempts: 0
      };
    }, trackerLabel);
  } catch (error) {
    appendDebugBuffer('cart_update_exception', {
      trackerLabel,
      message: String(error)
    });
    tracker?.mark(`${trackerLabel}_error`, {
      durationMs: Number((performance.now() - startedAt).toFixed(1)),
      message: String(error)
    });

    return {
      ok: false,
      cart: null,
      error
    };
  }
}

// -----------------------------------------------------------------------------
// Section targeting and DOM replacement
// -----------------------------------------------------------------------------

function getPrimaryPersonalizationContainer(sectionElement) {
  if (!sectionElement) return null;

  if (sectionElement.matches?.('[data-personalization-container]')) {
    return sectionElement;
  }

  // Prefer section-level container and ignore nested block-level containers.
  return sectionElement.querySelector(':scope > [data-personalization-container]') ||
    sectionElement.querySelector('[data-personalization-container]');
}

function getPrimaryPersonalizationContainerFromHtml(sectionHtml) {
  const wrapper = document.createElement('div');
  wrapper.innerHTML = sectionHtml;

  if (wrapper.firstElementChild?.matches?.('[data-personalization-container]')) {
    return wrapper.firstElementChild;
  }

  return wrapper.querySelector(':scope > [data-personalization-container]') ||
    wrapper.querySelector('[data-personalization-container]');
}

function hasNestedPersonalizationTrigger(element) {
  return Boolean(element?.querySelector?.('[data-personalization-section-trigger]'));
}

function hasNestedPersonalizationTriggerInHtml(sectionHtml) {
  return typeof sectionHtml === 'string' && sectionHtml.includes('data-personalization-section-trigger');
}

function dispatchPersonalizationSectionsUpdated(detail = {}) {
  document.dispatchEvent(new CustomEvent('mb:purchaseoption:sectionrefresh', {
    detail
  }));
}

function applySectionUpdates(sectionIds, sectionsData, options = {}) {
  const {
    stateOnly = false,
    compareMode = 'full-state',
    tracker = null,
    label = 'unknown',
    markRendered = true
  } = options;

  const metrics = {
    requestedCount: sectionIds.length,
    replacedCount: 0,
    unchangedCount: 0,
    missingCount: 0
  };
  const replacedSectionIds = [];
  const emitSectionStateDiagnostics = Boolean(getRuntimeOption('sectionStateDiagnosticsEnabled'));
  const sectionDiagnostics = [];

  sectionIds.forEach(sectionId => {
    const sectionElement = getSectionElement(sectionId);
    const nextHtml = sectionsData[sectionId];

    if (!sectionElement || typeof nextHtml !== 'string') {
      metrics.missingCount += 1;
      if (emitSectionStateDiagnostics) {
        sectionDiagnostics.push({
          sectionId,
          status: 'missing',
          hasSectionElement: Boolean(sectionElement),
          hasNextHtml: typeof nextHtml === 'string'
        });
      }
      return;
    }

    const currentState = getPersonalizationStateFromElement(sectionElement);
    const nextState = getPersonalizationStateFromHtml(nextHtml);
    const currentNormalizedHtml = normalizeSectionHtmlForComparison(sectionElement.innerHTML);
    const nextNormalizedHtml = normalizeSectionHtmlForComparison(nextHtml);
    const samePersonalizationState = isSamePersonalizationState(currentState, nextState);
    const visibilityChanged = currentState.hidden !== nextState.hidden;
    const hasNestedTrigger = hasNestedPersonalizationTrigger(sectionElement) ||
      hasNestedPersonalizationTriggerInHtml(nextHtml);

    if (stateOnly && compareMode === 'visibility-only' && !visibilityChanged && !hasNestedTrigger) {
      metrics.unchangedCount += 1;
      if (emitSectionStateDiagnostics) {
        sectionDiagnostics.push({
          sectionId,
          status: 'unchanged-visibility-only',
          currentHidden: currentState.hidden,
          nextHidden: nextState.hidden,
          visibilityChanged,
          hasNestedTrigger,
          currentVisibilityMode: currentState.visibilityMode,
          nextVisibilityMode: nextState.visibilityMode
        });
      }
      return;
    }

    if (stateOnly && samePersonalizationState && !hasNestedTrigger) {
      metrics.unchangedCount += 1;
      if (emitSectionStateDiagnostics) {
        sectionDiagnostics.push({
          sectionId,
          status: 'unchanged-state-only',
          currentHidden: currentState.hidden,
          nextHidden: nextState.hidden,
          visibilityChanged,
          hasNestedTrigger,
          currentVisibilityMode: currentState.visibilityMode,
          nextVisibilityMode: nextState.visibilityMode
        });
      }
      return;
    }

    if (samePersonalizationState && currentNormalizedHtml === nextNormalizedHtml) {
      metrics.unchangedCount += 1;
      if (emitSectionStateDiagnostics) {
        sectionDiagnostics.push({
          sectionId,
          status: 'unchanged-identical-html',
          currentHidden: currentState.hidden,
          nextHidden: nextState.hidden,
          visibilityChanged,
          currentVisibilityMode: currentState.visibilityMode,
          nextVisibilityMode: nextState.visibilityMode
        });
      }
      return;
    }

    sectionElement.innerHTML = nextHtml;
    if (markRendered) {
      markSectionPersonalizedRendered(sectionElement);
    }
    metrics.replacedCount += 1;
    replacedSectionIds.push(sectionId);
    if (emitSectionStateDiagnostics) {
      sectionDiagnostics.push({
        sectionId,
        status: 'replaced',
        currentHidden: currentState.hidden,
        nextHidden: nextState.hidden,
        visibilityChanged,
        currentVisibilityMode: currentState.visibilityMode,
        nextVisibilityMode: nextState.visibilityMode
      });
    }

    sectionElement.querySelectorAll('script').forEach(oldScript => {
      const newScript = document.createElement('script');
      Array.from(oldScript.attributes).forEach(attr => {
        newScript.setAttribute(attr.name, attr.value);
      });
      newScript.textContent = oldScript.textContent;
      oldScript.parentNode.replaceChild(newScript, oldScript);
    });
  });

  if (replacedSectionIds.length > 0) {
    dispatchPersonalizationSectionsUpdated({
      sectionIds: replacedSectionIds,
      source: 'mb-personalization'
    });
  }

  if (emitSectionStateDiagnostics) {
    tracker?.mark('sections_dom_state_diagnostics', {
      label,
      sectionDiagnostics
    });
  }

  return metrics;
}

function getPersonalizationStateFromElement(sectionElement) {
  const container = getPrimaryPersonalizationContainer(sectionElement);

  return getNormalizedPersonalizationState(container);
}

function getNormalizedPersonalizationState(container) {
  const rawPosition = container?.getAttribute('data-position');

  return {
    hidden: Boolean(container?.hasAttribute('data-personalization-hidden')),
    position: rawPosition ? Number.parseInt(rawPosition, 10) || null : null,
    behavior: normalizeQuotedAttributeValue(container?.dataset?.personalizationBehavior || 'visibility').toLowerCase(),
    visibilityMode: normalizeQuotedAttributeValue(container?.dataset?.personalizationVisibilityMode || 'unknown').toLowerCase(),
    loadPriority: normalizeQuotedAttributeValue(container?.dataset?.loadPriority || 'normal').toLowerCase()
  };
}

function markSectionPersonalizedRendered(sectionElement) {
  const container = getPrimaryPersonalizationContainer(sectionElement);
  if (!container) return;
  container.setAttribute('data-personalized-rendered', 'true');
}

function clearSectionPersonalizedRendered(sectionElement) {
  const container = getPrimaryPersonalizationContainer(sectionElement);
  if (!container) return;
  container.removeAttribute('data-personalized-rendered');
}

function getPersonalizationStateFromHtml(sectionHtml) {
  const container = getPrimaryPersonalizationContainerFromHtml(sectionHtml);

  return getNormalizedPersonalizationState(container);
}

function isSamePersonalizationState(currentState, nextState) {
  return currentState.hidden === nextState.hidden &&
    currentState.position === nextState.position &&
    currentState.behavior === nextState.behavior &&
    currentState.visibilityMode === nextState.visibilityMode &&
    currentState.loadPriority === nextState.loadPriority;
}

function normalizeSectionHtmlForComparison(sectionHtml) {
  const template = document.createElement('template');
  template.innerHTML = sectionHtml;

  // Ignore script tag churn (e.g. tokenized/nonce payloads) when deciding DOM replacement necessity.
  // TODO: Compare a narrower server-owned subtree or strip known runtime-only DOM mutations
  // (e.g. carousel wrappers, modal state, injected classes/styles) to reduce false-positive swaps.
  template.content.querySelectorAll('script').forEach(script => script.remove());

  return (template.innerHTML || '').replace(/\s+/g, ' ').trim();
}

  function getRegexMatchSegments() {
    const currentUrl = window.location.pathname + window.location.search;
    const matches = [];

    for (const [segmentName, segmentData] of Object.entries(window.MB_ACTIVE_SEGMENTS || {})) {
      const regexPattern = typeof segmentData === 'object' ? segmentData.regex : segmentData;

      if (!regexPattern) {
        const userType = segmentData.user_type || '';
        if (userType && userType.toLowerCase() !== 'none') continue;
        matches.push(segmentName); // SaleOnSale ("None") and any truly blank user_type both get pushed
        continue;
      }

      try {
        const regex = new RegExp(regexPattern, 'i');
        if (regex.test(currentUrl)) {
          matches.push(segmentName);
        }
      } catch (e) {
        console.warn('URL Detection ERROR: Invalid regex pattern for segment:', segmentName, regexPattern, e);
      }
    }

    return matches;
  }

  function isSegmentSessionExpired() {
    try {
      var lastSeen = Number(localStorage.getItem('ig_last_seen_at'));
      if (!lastSeen || isNaN(lastSeen)) return false;
      var minutesSinceLastSeen = (Date.now() - lastSeen) / 60000;
      return minutesSinceLastSeen > MB_SEGMENT_SESSION_TIMEOUT_MINUTES;
    } catch (e) {
      return false;
    }
  }
  
  function isUserTypeSegment(segmentName) {
    const normalizeSegment = s => s.toLowerCase().replace(/[_\-\s]/g, '');
    const normalizedInput = normalizeSegment(segmentName);
    
    const matchedKey = Object.keys(window.MB_ACTIVE_SEGMENTS || {}).find(
      key => normalizeSegment(key) === normalizedInput
    );
    
    const data = matchedKey ? window.MB_ACTIVE_SEGMENTS[matchedKey] : null;
    if (!data || typeof data !== 'object') return false;
    const userType = data.user_type || '';
    return userType && userType.toLowerCase() !== 'none';
  }

  // "Segment Can Be Invalidated" metafield — true means qualifying for a new
  // segment should drop this one (and its campaign) from the cart.
  function isInvalidatableSegment(segmentName) {
    const normalizeSegment = s => s.toLowerCase().replace(/[_\-\s]/g, '');
    const normalizedInput = normalizeSegment(segmentName);

    const matchedKey = Object.keys(window.MB_ACTIVE_SEGMENTS || {}).find(
      key => normalizeSegment(key) === normalizedInput
    );

    const data = matchedKey ? window.MB_ACTIVE_SEGMENTS[matchedKey] : null;
    if (!data || typeof data !== 'object') return false;
    return data.can_invalidate === true || data.can_invalidate === 'true';
  }

  function resolveCampaignInfo(matchedSegments) {
    const normalizeSegment = s => s.toLowerCase().replace(/[_\-\s]/g, '');
    let bestCampaign = null;
    let bestSpecificity = -1;

    for (const segmentName of matchedSegments) {
      const matchedKey = Object.keys(window.MB_ACTIVE_SEGMENTS || {}).find(
        key => normalizeSegment(key) === normalizeSegment(segmentName)
      );
      const segmentData = matchedKey ? window.MB_ACTIVE_SEGMENTS[matchedKey] : null;
      const campaign = segmentData?.campaign;

      if (!campaign?.campaign_id) {
        continue;
      }

      // Prefer the most specific matched segment so generic segments cannot
      // override channel-specific ones due to key iteration order.
      const specificity = normalizeSegment(segmentName).length;
      if (specificity > bestSpecificity) {
        bestSpecificity = specificity;
        bestCampaign = campaign;
      }
    }

    if (bestCampaign) {
      return bestCampaign;
    }
  
    // No segment matched — fall back to the theme's core/default campaign
    if (window.MB_DEFAULT_CAMPAIGN?.campaign_id) {
      return window.MB_DEFAULT_CAMPAIGN;
    }

    return null;
  }
  


  function buildBootstrapCartPayload() {
    const attrs = window.MB_BOOTSTRAP_CART_ATTRIBUTES;
    if (!attrs || typeof attrs !== 'object') return null;
  
    return {
      attributes: {
        segment: attrs.segment || '',
        current_campaign_id: attrs.current_campaign_id || '',
        lp_url: attrs.lp_url || ''
      }
    };
  }

function getPersonalizationSectionGroups(tracker, options = {}) {
  const {
    strategy = 'all'
  } = options;

  const sections = Array.from(document.querySelectorAll('[id^="shopify-section-"]'));
  const containers = sections
    .map(section => {
      const container = getPrimaryPersonalizationContainer(section);
      const hasNestedTrigger = hasNestedPersonalizationTrigger(section);
      if (!container && !hasNestedTrigger) return null;

      return {
        section,
        sectionId: section.id.replace('shopify-section-', ''),
        container,
        hasNestedTrigger
      };
    })
    .filter(Boolean);

  const selectedContainers = strategy === 'hidden-only'
    ? containers.filter(item => {
      if (item.hasNestedTrigger && !item.container) return true;

      const container = item.container;
      if (!container) return false;
      const behavior = normalizeQuotedAttributeValue(container.dataset.personalizationBehavior || 'visibility').toLowerCase();
      if (behavior !== 'visibility') return false;

      const visibilityMode = normalizeQuotedAttributeValue(container.dataset.personalizationVisibilityMode || 'unknown').toLowerCase();
      const segmentChangeDirection = window.MB_SEGMENT_CHANGE_DIRECTION || 'unknown';
      const isHidden = container.hasAttribute('data-personalization-hidden');

      if (segmentChangeDirection === 'additions-only') {
        if (isHidden) {
          // Hidden sections only need refresh if added segments can reveal them.
          return visibilityMode === 'show-only' || visibilityMode === 'mixed' || visibilityMode === 'unknown';
        }

        return visibilityMode === 'hide-only' || visibilityMode === 'mixed' || visibilityMode === 'unknown';
      }

      if (isHidden) return true;

      return true;
    })
    : containers;

  const effectiveContainers = selectedContainers.length > 0 ? selectedContainers : containers;

  const sectionPriority = new Map();

  const isHighPriorityValue = value => {
    if (typeof value !== 'string') return false;
    const normalizedValue = normalizeQuotedAttributeValue(value).toLowerCase();
    if (!normalizedValue) return false;

    return normalizedValue === 'high' || normalizedValue.startsWith('high ') || normalizedValue.startsWith('high-');
  };

  effectiveContainers.forEach(item => {
    const sectionId = item.sectionId;
    const priority = normalizeQuotedAttributeValue(item.container?.dataset?.loadPriority || 'normal').toLowerCase();
    const existing = sectionPriority.get(sectionId);

    if (existing === 'high') return;
    sectionPriority.set(sectionId, isHighPriorityValue(priority) ? 'high' : 'normal');
  });

  const allSectionIds = Array.from(sectionPriority.keys());
  const highPriorityEnabled = Boolean(window.MB_PRIORITY_LOADING);
  const highPriorityIds = highPriorityEnabled
    ? allSectionIds.filter(id => sectionPriority.get(id) === 'high')
    : [];
  const normalPriorityIds = highPriorityEnabled
    ? allSectionIds.filter(id => sectionPriority.get(id) !== 'high')
    : allSectionIds;

  return {
    allSectionIds,
    highPriorityIds,
    normalPriorityIds
  };
}

async function fetchSectionPayload(sectionIds, options) {
  const {
    tracker,
    label,
    batchEnabled,
    batchSize
  } = options;

  if (!sectionIds.length) {
    tracker?.mark('sections_fetch_skipped_empty', { label });
    return {
      sectionsData: {},
      fetchDurationMs: 0
    };
  }

  const baseUrl = window.location.pathname + window.location.search;
  const separator = baseUrl.includes('?') ? '&' : '?';
  const fetchStartedAt = performance.now();

  if (!batchEnabled) {
    tracker?.mark('sections_fetch_mode', {
      label,
      mode: 'single',
      requestCount: sectionIds.length
    });

    const payloads = await Promise.all(
      sectionIds.map(async (sectionId, index) => {
        const startedAt = performance.now();
        const cacheBust = `${Date.now()}-${index}`;
        const request = buildSectionFetchRequest(baseUrl, separator, sectionId, cacheBust);
        tracker?.mark('section_fetch_request_built', {
          label,
          sectionIds: [sectionId]
        });
        const response = await fetch(request.url, request.requestOptions);
        if (!response.ok) throw new Error(`Section fetch failed: ${response.status}`);

        const payload = await response.json();
        tracker?.mark('sections_batch_fetched', {
          label,
          index,
          sectionIds: [sectionId],
          durationMs: Number((performance.now() - startedAt).toFixed(1))
        });

        return payload;
      })
    );

    return {
      sectionsData: Object.assign({}, ...payloads),
      fetchDurationMs: Number((performance.now() - fetchStartedAt).toFixed(1))
    };
  }

  const batches = [];
  for (let i = 0; i < sectionIds.length; i += batchSize) {
    batches.push(sectionIds.slice(i, i + batchSize));
  }

  tracker?.mark('sections_update_batches_prepared', {
    label,
    sectionCount: sectionIds.length,
    batchCount: batches.length,
    batchSize
  });

  const payloads = await Promise.all(
    batches.map(async (batch, index) => {
      const startedAt = performance.now();
      const cacheBust = `${Date.now()}-${index}`;
      const request = buildSectionFetchRequest(baseUrl, separator, batch.join(','), cacheBust);
      tracker?.mark('section_fetch_request_built', {
        label,
        sectionIds: batch
      });
      const response = await fetch(request.url, request.requestOptions);
      if (!response.ok) throw new Error(`Section fetch failed: ${response.status}`);

      const payload = await response.json();
      tracker?.mark('sections_batch_fetched', {
        label,
        index,
        sectionIds: batch,
        durationMs: Number((performance.now() - startedAt).toFixed(1))
      });

      return payload;
    })
  );

  return {
    sectionsData: Object.assign({}, ...payloads),
    fetchDurationMs: Number((performance.now() - fetchStartedAt).toFixed(1))
  };
}

function getConditionalPersonalizationTargets(excludedSectionIds = new Set()) {
  return Array.from(document.querySelectorAll('[data-conditional-personalization][data-conditional-id]'))
    .map(container => {
      const section = container.closest('[id^="shopify-section-"]');
      if (!section) return null;

      const sectionId = section.id.replace('shopify-section-', '');
      if (excludedSectionIds.has(sectionId)) return null;

      return {
        container,
        conditionalId: container.dataset.conditionalId,
        sectionId
      };
    })
    .filter(Boolean);
}

function normalizeConditionalSegment(value) {
  return String(value || '').toLowerCase().replace(/[_\-\s]/g, '');
}

function resolveClientConditionalCandidates(segments, tracker) {
  const resolvedSegments = new Set(segments.map(normalizeConditionalSegment).filter(Boolean));
  let resolvedCount = 0;

  document.querySelectorAll('[data-conditional-personalization]').forEach(container => {
    // Includes the else-clause item, which never carries a -mode attribute.
    const items = Array.from(container.querySelectorAll('[data-conditional-item], [data-conditional-candidate]'));
    if (items.length === 0) return;

    // Hide everything first: SSR can render multiple simultaneous candidates
    // (blank-cart optimistic branch) plus the else clause in the same pass.
    items.forEach(item => {
      item.hidden = true;
    });

    const winner = items.find(item => {
      if (item.dataset.isElse === 'true') return false;

      const mode = item.dataset.conditionalPersonalizationMode || 'show';
      const itemSegments = (item.dataset.conditionalPersonalizationSegments || '')
        .split('|')
        .map(normalizeConditionalSegment)
        .filter(Boolean);
      const matchesSegment = itemSegments.some(segment => resolvedSegments.has(segment));

      if (mode === 'show') return matchesSegment;
      if (mode === 'exclude') return !matchesSegment;
      return false;
    });

    const elseItem = items.find(item => item.dataset.isElse === 'true') || null;
    const selectedItem = winner || elseItem;

    if (!selectedItem) {
      container.dataset.conditionalMatched = 'false';
      container.dataset.conditionalClientResolved = 'true';
      return;
    }

    selectedItem.hidden = false;
    container.dataset.conditionalMatched = winner ? 'true' : 'false';

    // Reveal immediately for UX, but keep data-conditional-personalization in
    // place so this optimistic client-side pick still gets re-checked and
    // corrected by the authoritative mb_personalization-check server
    // validator (updateConditionalPersonalizations) later in the pipeline.
    container.dataset.conditionalClientResolved = 'true';
    resolvedCount += 1;
  });

  tracker?.mark('conditional_personalization_client_resolved', {
    resolvedCount,
    segmentCount: resolvedSegments.size
  });
}

function resolveConditionalPersonalizationTargets(targets, sectionsData) {
  const templatesBySectionId = new Map();

  const getSectionTemplate = sectionId => {
    if (templatesBySectionId.has(sectionId)) return templatesBySectionId.get(sectionId);

    const sectionHtml = sectionsData[sectionId];
    let template = null;
    if (typeof sectionHtml === 'string') {
      template = document.createElement('template');
      template.innerHTML = sectionHtml;
    }

    templatesBySectionId.set(sectionId, template);
    return template;
  };

  return targets.map(({ container, conditionalId, sectionId }) => {
    const template = getSectionTemplate(sectionId);
    if (!template) {
      return { container, conditionalId, sectionId, nextContainer: null, status: 'missing-section-html' };
    }

    const nextContainer = Array.from(template.content.querySelectorAll('[data-conditional-personalization][data-conditional-id]'))
      .find(candidate => candidate.dataset.conditionalId === conditionalId);

    if (!nextContainer) {
      return {
        container,
        conditionalId,
        sectionId,
        nextContainer: null,
        status: 'missing-conditional-html',
        responseConditionalIds: Array.from(template.content.querySelectorAll('[data-conditional-personalization][data-conditional-id]'))
          .map(candidate => candidate.dataset.conditionalId)
      };
    }

    return { container, conditionalId, sectionId, nextContainer, status: 'resolved' };
  });
}

function applyConditionalVisibility(container) {
  const items = Array.from(
    container.querySelectorAll(
      '[data-conditional-item], [data-conditional-candidate]'
    )
  );

  const matched = container.dataset.conditionalMatched === 'true';

  if (!matched) {
    const elseItem = items.find(item => item.dataset.isElse === 'true');

    items.forEach(item => {
      item.hidden = item !== elseItem;
    });

    return;
  }

  // If server says a condition matched, we don't want
  // to blindly reveal every item.
  //
  // The server-rendered HTML should ideally already contain
  // only the matched item here.
}

function applyResolvedConditionalPersonalizationTargets(resolved) {
  let replacedCount = 0;
  let missingCount = 0;
  const targetResults = [];

  resolved.forEach(({ container, conditionalId, sectionId, nextContainer, status, responseConditionalIds }) => {
    if (!nextContainer) {
      missingCount += 1;
      targetResults.push({ conditionalId, sectionId, status, responseConditionalIds });
      return;
    }

    container.innerHTML = nextContainer.innerHTML;
    container.dataset.conditionalMatched = nextContainer.dataset.conditionalMatched || 'false';
    applyConditionalVisibility(container);
    // Reveal immediately rather than waiting on reorder/global personalization steps.
    container.removeAttribute('data-conditional-personalization');
    delete container.dataset.conditionalClientResolved;
    container.querySelectorAll('script').forEach(oldScript => {
      const newScript = document.createElement('script');
      Array.from(oldScript.attributes).forEach(attr => {
        newScript.setAttribute(attr.name, attr.value);
      });
      newScript.textContent = oldScript.textContent;
      oldScript.parentNode.replaceChild(newScript, oldScript);
    });
    replacedCount += 1;
    targetResults.push({
      conditionalId,
      sectionId,
      status: 'replaced',
      matched: nextContainer.dataset.conditionalMatched || 'false',
      result: nextContainer.querySelector('[data-condition-result]')?.dataset.conditionResult || 'none'
    });
  });

  return { replacedCount, missingCount, targetResults };
}

async function updateConditionalPersonalizations(tracker, excludedSectionIds = []) {
  const targets = getConditionalPersonalizationTargets(new Set(excludedSectionIds));
  const sectionIds = Array.from(new Set(targets.map(target => target.sectionId)));

  if (!sectionIds.length) {
    const conditionalContainers = Array.from(document.querySelectorAll('[data-conditional-personalization]'));
    const diagnosticDetails = {
      excludedSectionIds: Array.from(excludedSectionIds),
      conditionalContainerCount: conditionalContainers.length,
      conditionalOwnerSectionIds: conditionalContainers
        .map(container => container.closest('[id^="shopify-section-"]')?.id || '')
        .filter(Boolean)
    };
    tracker?.mark('conditional_personalization_skipped_no_targets', diagnosticDetails);
    return { sectionCount: 0, replacedCount: 0, missingCount: 0 };
  }

  // The container's dataset.conditionalMatched already reflects the client-side
  // resolve pass (based on the segments just written to the cart). If the
  // authoritative server fetch disagrees, that's most likely the Section
  // Rendering API reading a cart snapshot that hasn't caught up yet with the
  // /cart/update.js write, rather than a genuine mismatch — retry once.
  const expectedMatchByConditionalId = new Map(
    targets.map(target => [target.conditionalId, target.container.dataset.conditionalMatched || 'false'])
  );

  const fetchAndResolve = async label => {
    const payload = await fetchSectionPayload(sectionIds, {
      tracker,
      label,
      batchEnabled: Boolean(window.MB_BATCH_ENABLED),
      batchSize: MB_DEFAULT_BATCH_SIZE
    });
    tracker?.mark('conditional_personalization_response', {
      sectionIds,
      responseLengths: Object.fromEntries(sectionIds.map(sectionId => [sectionId, payload.sectionsData[sectionId]?.length || 0]))
    });
    const resolved = resolveConditionalPersonalizationTargets(targets, payload.sectionsData);
    return { resolved, fetchDurationMs: payload.fetchDurationMs };
  };

  let { resolved, fetchDurationMs } = await fetchAndResolve('conditional-personalization');

  const detectDrift = () => resolved.filter(({ conditionalId, nextContainer }) => {
    const expectedMatched = expectedMatchByConditionalId.get(conditionalId);
    return expectedMatched === 'true' && nextContainer && nextContainer.dataset.conditionalMatched !== 'true';
  });

  let drifted = detectDrift();

  if (drifted.length > 0) {
    tracker?.mark('conditional_personalization_drift_detected', {
      conditionalIds: drifted.map(target => target.conditionalId)
    });
    await waitFor(MB_CART_SYNC_VERIFY_DELAY_MS);
    ({ resolved, fetchDurationMs } = await fetchAndResolve('conditional-personalization-retry'));
    drifted = detectDrift();
  }

  if (drifted.length > 0) {
    // The server still disagrees with a client resolution we're confident in
    // (matches the segment just persisted to the cart) — most likely the
    // Section Rendering API hasn't caught up yet. Don't overwrite a known-good
    // optimistic render with stale/inconsistent content; leave it as-is.
    const driftedIds = new Set(drifted.map(target => target.conditionalId));
    tracker?.mark('conditional_personalization_drift_persisted', {
      conditionalIds: Array.from(driftedIds)
    });
    resolved = resolved.filter(target => !driftedIds.has(target.conditionalId));
  }

  const metrics = applyResolvedConditionalPersonalizationTargets(resolved);

  tracker?.mark('conditional_personalization_updated', {
    sectionCount: sectionIds.length,
    conditionalIds: targets.map(target => target.conditionalId),
    sectionIds,
    retried: drifted.length > 0 || resolved.length !== targets.length,
    skippedDriftedCount: targets.length - resolved.length,
    ...metrics,
    fetchDurationMs
  });

  return { sectionCount: sectionIds.length, ...metrics };
}

async function updatePersonalizedSections(tracker) {
  try {
    const sectionsUpdateStartedAt = performance.now();
    tracker?.mark('sections_update_start');

    const shouldFetchHiddenOnly = Boolean(window.MB_FETCH_HIDDEN_ONLY);
    const fetchStrategy = shouldFetchHiddenOnly ? 'hidden-only' : 'all';
    const stateOnlyApply = shouldFetchHiddenOnly;
    const segmentChangeDirection = window.MB_SEGMENT_CHANGE_DIRECTION || 'unknown';
    const compareMode = (shouldFetchHiddenOnly && segmentChangeDirection === 'additions-only')
      ? 'visibility-only'
      : 'full-state';

    tracker?.mark('sections_update_strategy', {
      fetchStrategy,
      compareMode,
      segmentChangeDirection
    });

    const {
      allSectionIds,
      highPriorityIds,
      normalPriorityIds
    } = getPersonalizationSectionGroups(tracker, { strategy: fetchStrategy });

    tracker?.mark('sections_fetch_targets', {
      allSectionIds,
      highPriorityIds,
      normalPriorityIds
    });

    if (allSectionIds.length === 0) {
      tracker?.mark('sections_update_skipped_no_section_ids');
      await updateConditionalPersonalizations(tracker);
      revealPersonalizedContent(tracker, 'no-section-ids');
      return {
        sectionCount: 0,
        replacedCount: 0,
        unchangedCount: 0,
        missingCount: 0,
        transitionSummary: {}
      };
    }

    const highPriorityFetchPromise = fetchSectionPayload(highPriorityIds, {
      tracker,
      label: 'high-priority',
      batchEnabled: true,
      batchSize: MB_DEFAULT_BATCH_SIZE
    });

    const normalPriorityFetchPromise = fetchSectionPayload(normalPriorityIds, {
      tracker,
      label: 'normal-priority',
      batchEnabled: Boolean(window.MB_BATCH_ENABLED),
      batchSize: MB_DEFAULT_BATCH_SIZE
    });

    const highPriorityFetchResult = await highPriorityFetchPromise;
    const highPriorityPayload = highPriorityFetchResult.sectionsData;
    let highPriorityMetrics = {
      requestedCount: 0,
      replacedCount: 0,
      unchangedCount: 0,
      missingCount: 0
    };
    let highPriorityApplyMs = 0;

    if (highPriorityIds.length > 0) {
      const highPriorityApplyStartedAt = performance.now();
      highPriorityMetrics = applySectionUpdates(highPriorityIds, highPriorityPayload, {
        stateOnly: stateOnlyApply,
        compareMode,
        tracker,
        label: 'high-priority'
      });
      highPriorityApplyMs = Number((performance.now() - highPriorityApplyStartedAt).toFixed(1));
      tracker?.mark('sections_dom_replaced_high_priority', highPriorityMetrics);

      revealHighPriorityContent(tracker, 'high-priority-sections-ready');
    }

    const normalPriorityFetchResult = await normalPriorityFetchPromise;
    const normalPriorityPayload = normalPriorityFetchResult.sectionsData;
    const normalPriorityApplyStartedAt = performance.now();
    const normalPriorityMetrics = applySectionUpdates(normalPriorityIds, normalPriorityPayload, {
      stateOnly: stateOnlyApply,
      compareMode,
      tracker,
      label: 'normal-priority'
    });
    const normalPriorityApplyMs = Number((performance.now() - normalPriorityApplyStartedAt).toFixed(1));
    tracker?.mark('sections_dom_replaced_normal_priority', normalPriorityMetrics);

    const totalReplacedCount = highPriorityMetrics.replacedCount + normalPriorityMetrics.replacedCount;
    const totalUnchangedCount = highPriorityMetrics.unchangedCount + normalPriorityMetrics.unchangedCount;
    const totalMissingCount = highPriorityMetrics.missingCount + normalPriorityMetrics.missingCount;

    tracker?.mark('sections_dom_replaced', {
      sectionCount: allSectionIds.length,
      replacedCount: totalReplacedCount,
      unchangedCount: totalUnchangedCount,
      missingCount: totalMissingCount
    });

    updateRunSummary({
      highPriorityFetchMs: Number(highPriorityFetchResult.fetchDurationMs || 0),
      highPriorityApplyMs,
      normalPriorityFetchMs: Number(normalPriorityFetchResult.fetchDurationMs || 0),
      normalPriorityApplyMs,
      sectionsUpdateTotalMs: Number((performance.now() - sectionsUpdateStartedAt).toFixed(1))
    });

    await updateConditionalPersonalizations(tracker, allSectionIds);

    reorderSections(tracker);

    return {
      sectionCount: allSectionIds.length,
      replacedCount: totalReplacedCount,
      unchangedCount: totalUnchangedCount,
      missingCount: totalMissingCount,
      transitionSummary: {}
    };
  } catch (error) {
    console.warn('Error updating personalized sections:', error);
    tracker?.mark('sections_update_error', { message: String(error) });
    revealPersonalizedContent(tracker, 'sections-update-error');
    return {
      sectionCount: 0,
      replacedCount: 0,
      unchangedCount: 0,
      missingCount: 0,
      transitionSummary: {},
      error: true
    };
  }
}

// -----------------------------------------------------------------------------
// Reorder and global personalization
// -----------------------------------------------------------------------------

function reorderSections(tracker) {
  if (window.mbReorderInProgress) {
    tracker?.mark('reorder_skipped_in_progress');
    return;
  }

  window.mbReorderInProgress = true;
  const reorderStart = performance.now();

  try {
    tracker?.mark('reorder_start');

    // Get ALL section children from common parents
    const personalizedWrappers = Array.from(document.querySelectorAll('[data-personalization-container]'))
      .map(container => container.closest('.shopify-section, [id^="shopify-section-"]'))
      .filter(Boolean);

    const uniquePersonalizedWrappers = Array.from(new Set(personalizedWrappers));

    if (uniquePersonalizedWrappers.length === 0) {
      tracker?.mark('reorder_skipped_no_personalized_sections');
      revealPersonalizedContent(tracker, 'reorder-no-personalized-sections');
      return;
    }

    const primaryParent = uniquePersonalizedWrappers[0]?.parentElement || null;
    if (!primaryParent) {
      tracker?.mark('reorder_skipped_no_primary_parent');
      revealPersonalizedContent(tracker, 'reorder-no-primary-parent');
      return;
    }

    // Get ALL section children from the parent, not just personalized ones
    const allSectionChildren = Array.from(primaryParent.children)
      .filter(child => {
        return child.classList.contains('shopify-section') || 
               child.id?.startsWith('shopify-section-');
      })
      .filter(child => {
        // Exclude footers
        return !child.id?.includes('__footer') && !child.closest('footer');
      });

    tracker?.mark('reorder_all_sections_identified', {
      totalSectionCount: allSectionChildren.length
    });

    // Categorize sections
    const categorizedSections = allSectionChildren.map((wrapper, originalIndex) => {
      const container = wrapper.querySelector('[data-personalization-container]');
      const isPersonalized = container !== null;
      const isHidden = container?.hasAttribute('data-personalization-hidden') || false;
      const position = container ? parseInt(container.getAttribute('data-position'), 10) : 0;
      const isPositioned = position > 0;

      return {
        element: wrapper,
        isPersonalized,
        isHidden,
        position: isPositioned ? position : null,
        originalIndex
      };
    });

    // Hidden sections stay in place (marked with data-personalization-hidden)
    const visibleSections = categorizedSections.filter(s => !s.isHidden);
    const hiddenSections = categorizedSections.filter(s => s.isHidden);

    // Split visible sections into positioned personalized and everything else
    const positionedPersonalized = visibleSections
      .filter(s => s.isPersonalized && s.position !== null)
      .sort((a, b) => a.position - b.position);

    // Non-positioned sections (both personalized and non-personalized) maintain original order
    const nonPositionedSections = visibleSections
      .filter(s => !s.isPositioned)
      .sort((a, b) => a.originalIndex - b.originalIndex);

    // If no positioned personalized sections, no reordering needed
    if (positionedPersonalized.length === 0) {
      tracker?.mark('reorder_skipped_no_positioned_sections');
      revealPersonalizedContent(tracker, 'reorder-no-positioned');
      return;
    }

    // Build target order by interleaving positioned and non-positioned sections
    // Positioned personalized sections go at their specified positions
    // Non-positioned sections (both personalized and non-personalized) fill the other slots in original order
    const positionMap = [];
    let nonPositionedIdx = 0;

    // Fill position slots from 1 to maxPosition
    const maxPosition = Math.max(...positionedPersonalized.map(s => s.position), nonPositionedSections.length);
    
    for (let pos = 1; pos <= maxPosition; pos++) {
      const positionedSection = positionedPersonalized.find(s => s.position === pos);
      if (positionedSection) {
        positionMap.push(positionedSection);
      } else if (nonPositionedIdx < nonPositionedSections.length) {
        positionMap.push(nonPositionedSections[nonPositionedIdx]);
        nonPositionedIdx += 1;
      }
    }

    // Add any remaining non-positioned sections that didn't fit in the position map
    const targetOrder = [...positionMap];
    while (nonPositionedIdx < nonPositionedSections.length) {
      targetOrder.push(nonPositionedSections[nonPositionedIdx]);
      nonPositionedIdx += 1;
    }

    // Add hidden sections at end
    hiddenSections.forEach(section => {
      targetOrder.push(section);
    });

    const targetElements = targetOrder.map(s => s.element);

    // Re-append all sections in target order
    targetElements.forEach(element => primaryParent.appendChild(element));

    tracker?.mark('reorder_complete', {
      totalSectionCount: allSectionChildren.length,
      positionedPersonalizedCount: positionedPersonalized.length,
      nonPositionedCount: nonPositionedSections.length,
      hiddenCount: hiddenSections.length,
      durationMs: Number((performance.now() - reorderStart).toFixed(1))
    });

    revealPersonalizedContent(tracker, 'reorder-complete');
  } finally {
    window.mbReorderInProgress = false;
  }
}

function getGlobalPersonalizationConfig() {
  let gpConfig = globalThis.globalPersonalizations;

  if (gpConfig && typeof gpConfig === 'object') {
    return gpConfig;
  }

  const rebuildConfig = globalThis.MB_BUILD_GLOBAL_PERSONALIZATIONS;
  if (typeof rebuildConfig !== 'function') {
    return null;
  }

  try {
    gpConfig = rebuildConfig();
    if (gpConfig && typeof gpConfig === 'object') {
      globalThis.globalPersonalizations = gpConfig;
      appendDebugBuffer('global_personalization_config_rehydrated', {
        availableKeys: Object.keys(gpConfig).length
      });
      return gpConfig;
    }
  } catch (error) {
    appendDebugBuffer('global_personalization_config_rehydrate_failed', {
      message: String(error)
    });
  }

  return null;
}


//ADDING GLOBAL PERSONALIZATION BASED ON SEGMENTS AND METAOBJECTS
function processGlobalPersonalization(gpsegments){
  const gpConfig = getGlobalPersonalizationConfig();
  const report = {
    skippedNoConfig: false,
    availableKeys: 0,
    inputSegments: Array.isArray(gpsegments) ? gpsegments.slice() : [],
    matchedSegments: [],
    missedSegments: [],
    redirectRules: 0,
    redirectMatchedLinks: 0,
    redirectReplacedLinks: 0,
    errors: []
  };

  if (!gpConfig || typeof gpConfig !== 'object') {
  appendDebugBuffer('global_personalization_skipped_no_config', {
    segmentCount: Array.isArray(gpsegments) ? gpsegments.length : 0
  });
  report.skippedNoConfig = true;
  return report;
  }

  const availableKeys = Object.keys(gpConfig || {});
  report.availableKeys = availableKeys.length;
  const normalizedSegments = Array.isArray(gpsegments)
    ? gpsegments.map(segment => segment?.toLowerCase().replace(/[_-\s]/g, '')).filter(Boolean)
    : [];
  const matchedSegmentKeys = new Set(
    normalizedSegments.filter(segment => gpConfig[segment] != null)
  );
  appendDebugBuffer('global_personalization_start', {
    rawSegments: Array.isArray(gpsegments) ? gpsegments : [],
    availableKeys
  });

  let gpsegment = 0;
  let thisAdjustedSegment = null;

  while (gpsegment < gpsegments.length){
      thisAdjustedSegment = gpsegments[gpsegment]?.toLowerCase().replace(/[_-\s]/g,"");

    if (gpConfig[thisAdjustedSegment] != null){
      report.matchedSegments.push(thisAdjustedSegment);
      appendDebugBuffer('global_personalization_segment_match', {
        segment: gpsegments[gpsegment],
        normalizedSegment: thisAdjustedSegment
      });
      try {
        //THIS ONE IS ACTIVE
        //RUN THE JAVASCRIPT
        eval(gpConfig[thisAdjustedSegment].custom_js);

        //EMBED THE CSS
        const segmentstyles = document.createElement('style');
        segmentstyles.textContent = gpConfig[thisAdjustedSegment].custom_css;
        document.head.appendChild(segmentstyles);

        //IMPLEMENT THE REDIRECTS
        let redirect = 0;
        const redirects = gpConfig[thisAdjustedSegment]?.redirects || [];
        report.redirectRules += redirects.length;

        while (redirect < redirects.length){
          const redirectsource = redirects[redirect].source;
          const redirectdestination = redirects[redirect].destination;
          const selector = 'a[href*="' + redirectsource + '"]';
          const matchingLinks = Array.from(document.querySelectorAll(selector));
          let replacedCount = 0;
          report.redirectMatchedLinks += matchingLinks.length;

          matchingLinks.forEach(link => {
          const currentHref = link.getAttribute('href');
          if (currentHref != redirectdestination && currentHref.indexOf(redirectsource) > -1 && currentHref != window.MB_RESOLVED_LP_URL) {
            link.setAttribute('href', currentHref.replace(redirectsource, redirectdestination));
            replacedCount += 1;
          }
          });
          report.redirectReplacedLinks += replacedCount;

          redirect++;
        }
      } catch (error) {
        console.warn('Global personalization segment execution failed:', thisAdjustedSegment, error);
        report.errors.push({
          segment: thisAdjustedSegment,
          message: String(error)
        });
        appendDebugBuffer('global_personalization_segment_error', {
          normalizedSegment: thisAdjustedSegment,
          message: String(error)
        });
      }
      } else {
        const inheritedFromParent = Array.from(matchedSegmentKeys).find(
          parentSegment => thisAdjustedSegment.startsWith(parentSegment) && thisAdjustedSegment !== parentSegment
        );

        if (inheritedFromParent) {
          gpsegment++;
          continue;
        }

        report.missedSegments.push(thisAdjustedSegment);
        appendDebugBuffer('global_personalization_segment_miss', {
          segment: gpsegments[gpsegment],
          normalizedSegment: thisAdjustedSegment
        });
      }
     gpsegment++; 
  }

  appendDebugBuffer('global_personalization_complete', {
    rawSegments: Array.isArray(gpsegments) ? gpsegments : []
  });

  return report;

}

window.processGlobalPersonalization = processGlobalPersonalization;


// -----------------------------------------------------------------------------
// Main runtime pipeline
// -----------------------------------------------------------------------------


(async function () {
  const tracker = createPersonalizationTracker();
  let fallbackRevealTimer;

  try {
    tracker.mark('pipeline_start', {
      url: window.location.pathname + window.location.search,
      domState: document.readyState
    });

    document.documentElement.classList.add(MB_PERSONALIZATION_PENDING_CLASS);

    fallbackRevealTimer = window.setTimeout(() => {
      tracker.mark('failsafe_reveal_triggered', { timeoutMs: MB_PERSONALIZATION_FAILSAFE_TIMEOUT_MS });
      revealPersonalizedContent(tracker, 'failsafe-timeout');
    }, MB_PERSONALIZATION_FAILSAFE_TIMEOUT_MS);

    const regexMatchSegments = getRegexMatchSegments();
    tracker.mark('regex_segments_detected', {
      count: regexMatchSegments.length,
      segments: regexMatchSegments
    });

    const bootstrapCart = buildBootstrapCartPayload();
    let cartPromise;

    if (bootstrapCart) {
      tracker.mark('cart_bootstrap_used', {
        hasSegment: Boolean(bootstrapCart.attributes.segment),
        hasCampaignId: Boolean(bootstrapCart.attributes.current_campaign_id)
      });
      cartPromise = Promise.resolve(bootstrapCart);
    } else {
      tracker.mark('cart_bootstrap_missing');
      const cartFetchStarted = performance.now();
      cartPromise = fetch('/cart.js', { headers: { Accept: 'application/json' } })
        .then(response => response.ok ? response.json() : null)
        .then(cart => {
          tracker.mark('cart_fetch_complete', {
            durationMs: Number((performance.now() - cartFetchStarted).toFixed(1)),
            hasCart: Boolean(cart)
          });
          return cart;
        })
        .catch(error => {
          console.error('Cart fetch failed:', error);
          tracker.mark('cart_fetch_error', {
            durationMs: Number((performance.now() - cartFetchStarted).toFixed(1)),
            message: String(error)
          });
          return null;
        });
    }

    if (document.readyState === 'loading') {
      tracker.mark('waiting_for_dom_content_loaded');
      await new Promise(resolve => {
        document.addEventListener('DOMContentLoaded', resolve, { once: true });
      });
      tracker.mark('dom_content_loaded');
    }

    let cart = await cartPromise;

    // Bootstrap attributes can be stale on rapid navigation; always prefer a
    // live cart snapshot before deciding whether a write is needed.
    if (bootstrapCart) {
      const liveCart = await fetchCartSnapshot();
      if (liveCart?.attributes) {
        cart = {
          ...(cart || {}),
          ...liveCart,
          attributes: {
            ...(cart?.attributes || {}),
            ...(liveCart.attributes || {})
          }
        };
        tracker.mark('cart_bootstrap_refreshed_from_live', {
          hasSegment: Boolean(cart?.attributes?.segment),
          hasCampaignId: Boolean(cart?.attributes?.current_campaign_id)
        });
      }
    }


// Segment cleanup — handles both logout and session expiry in one cart update
if (cart?.attributes?.segment) {
  const existingSegments = cart.attributes.segment.split(',').map(s => s.trim()).filter(Boolean);
  let segmentsToKeep = [...existingSegments];

  let isLogout = false;

  // logged out — remove subscription segments
  if (!window.MB_IS_LOGGED_IN) {
    isLogout = existingSegments.some(seg => isUserTypeSegment(seg));
    segmentsToKeep = isLogout
      ? []   //user-type present + not logged in = logout, remove ALL segments
      : segmentsToKeep.filter(seg => !isUserTypeSegment(seg));
  }

  //  session expired — remove URL segments
  if (isSegmentSessionExpired()) {
    segmentsToKeep = window.MB_IS_LOGGED_IN
      ? segmentsToKeep.filter(seg => isUserTypeSegment(seg))
      : [];
  }

  const clearedSegment = segmentsToKeep.join(',');

  // Only update cart if something actually changed
  if (clearedSegment !== cart.attributes.segment) {
    const cleanupResult = await updateCartAttributesWithVerification(
      isLogout
        ? { segment: '', current_campaign_id: '', lp_url: '' }
        : { segment: clearedSegment },
      {
        expectedSegment: clearedSegment,
        tracker,
        trackerLabel: 'cart_cleanup_update'
      }
    );

    if (cleanupResult.ok && cart?.attributes) {
      cart.attributes.segment = clearedSegment;
    }
    if (isLogout && cleanupResult.ok) {
      try {
        localStorage.removeItem('mb_last_known_segments');
        const m = JSON.parse(sessionStorage.getItem('campaign_attributes') || '{}');
        delete m.current_campaign_id;
        delete m.lp_url;
        sessionStorage.setItem('campaign_attributes', JSON.stringify(m));
      } catch (e) {}
      if (window.location.pathname !== '/') {
        window.location.replace('/');
        return;
      }
    }
  }
}

    // ── localStorage cleanup — runs ALWAYS, even when cart is empty ──────────
    // Mirrors the same logout/expiry rules as the cart block above.
    try {
      const saved = localStorage.getItem('mb_last_known_segments');
      if (saved) {
        let localSegments = saved.split(',').map(s => s.trim()).filter(Boolean);

        if (!window.MB_IS_LOGGED_IN) {
          localSegments = localSegments.filter(s => !isUserTypeSegment(s));
        }

        if (isSegmentSessionExpired()) {
          localSegments = window.MB_IS_LOGGED_IN
            ? localSegments.filter(s => isUserTypeSegment(s))
            : [];
        }

        localStorage.setItem('mb_last_known_segments', localSegments.join(','));
      }
    } catch (e) { }

    // update last seen timestamp AFTER expiry check
    try {
      localStorage.setItem('ig_last_seen_at', String(Date.now()));
    } catch (e) { }

    let currentSegment = cart?.attributes?.segment || '';

    // Save to localStorage whenever cart has a valid segment (pre-order backup)
    if (currentSegment) {
      try {
        localStorage.setItem('mb_last_known_segments', currentSegment);
      } catch (e) { }
    }

    // Post-order recovery: restore from localStorage when cart is empty
    if (!currentSegment) {
      try {
        const saved = localStorage.getItem('mb_last_known_segments');
        if (saved) {
          currentSegment = saved;
          const restoreResult = await updateCartAttributesWithVerification(
            { segment: saved },
            {
              expectedSegment: saved,
              tracker,
              trackerLabel: 'cart_restore_update'
            }
          );
          if (restoreResult.ok && cart?.attributes) cart.attributes.segment = saved;
        }
        if (saved) currentSegment = saved;

      } catch (e) { }
    }

    const currentSegments = currentSegment && currentSegment !== 'null'
      ? currentSegment.split(',').map(segment => segment.trim()).filter(Boolean)
      : [];

    const activeSegmentNames = Object.keys(window.MB_ACTIVE_SEGMENTS || {});
    const normalizeSegment = s => s.toLowerCase().replace(/[_\-\s]/g, '');
    const activeSegmentNamesNormalized = activeSegmentNames.map(normalizeSegment);

    const validSegments = currentSegments.filter(segment =>
      activeSegmentNamesNormalized.includes(normalizeSegment(segment))
    );
    const hasExpiredSegments = validSegments.length !== currentSegments.length;
    const newSegments = regexMatchSegments.filter(segment => !validSegments.includes(segment));
    const userSegmentationChanged = newSegments.length > 0;

    // Qualifying for a new segment invalidates any currently-held segment
    // flagged "Segment Can Be Invalidated" (e.g. campaign LPs that shouldn't
    // co-exist), so its campaign attributes get dropped and recomputed below.
    const invalidatedSegments = userSegmentationChanged
      ? validSegments.filter(segment => isInvalidatableSegment(segment))
      : [];
    const hasInvalidatedSegments = invalidatedSegments.length > 0;
    const retainedValidSegments = hasInvalidatedSegments
      ? validSegments.filter(segment => !invalidatedSegments.includes(segment))
      : validSegments;

    if (hasInvalidatedSegments) {
      tracker.mark('segments_invalidated', {
        invalidatedSegments,
        newSegments
      });
    }

    // After computing validSegments, add:
   
    // Prioritize segments matched from the current URL over carried cart state.
    // This prevents stale segments from previous sessions/channels from taking precedence.
    const allCurrentSegments = Array.from(new Set([...regexMatchSegments, ...retainedValidSegments]));
    const resolvedCampaign = resolveCampaignInfo(allCurrentSegments);
    const resolvedCampaignId = resolvedCampaign?.campaign_id || null;
    const resolvedLpUrl = resolvedCampaign?.lp_url || null;

    // Logo href follows whichever campaign actually applies — segment match or
    // default fallback. Runs unconditionally so it also applies on reloads
    // where segments/campaign/lp_url are already unchanged (no-change branch).
    const logo = document.querySelector(".header-logo");
    if (logo && resolvedLpUrl) {
      logo.setAttribute("href", resolvedLpUrl);
      window.MB_RESOLVED_LP_URL = resolvedLpUrl;
    }

    // current_campaign_id is a plain string now, not JSON — compare directly
    const campaignIdChanged = resolvedCampaignId !== (cart?.attributes?.current_campaign_id || null);
    const lpUrlChanged = resolvedLpUrl !== (cart?.attributes?.lp_url || null);

    tracker.mark('segment_diff_computed', {
      currentSegmentCount: currentSegments.length,
      validSegmentCount: validSegments.length,
      regexMatchCount: regexMatchSegments.length,
      newSegmentCount: newSegments.length,
      hasExpiredSegments,
      hasInvalidatedSegments,
      invalidatedSegments,
      userSegmentationChanged,
      campaignIdChanged,
      lpUrlChanged
    });

      if (!userSegmentationChanged && !hasExpiredSegments && !campaignIdChanged && !lpUrlChanged) {
      // Reconcile the analytics session mirror even when nothing else changed:
      // this restores the campaign into sessionStorage for returning visitors
      // (whose campaign already lives in the cart) and clears a stale value when
      // the cart no longer holds an active campaign.
      syncCampaignSessionMirror(
        resolvedCampaignId || (cart && cart.attributes && cart.attributes.current_campaign_id) || '',
        resolvedLpUrl || ''
      );

      const gpNoChangeReport = processGlobalPersonalization(currentSegments);
      tracker.mark('global_personalization_executed', {
        source: 'no-change',
        segmentCount: currentSegments.length,
        segments: currentSegments,
        report: gpNoChangeReport
      });

      // Segments are unchanged, so the SSR content was already rendered against
      // the correct, still-current cart segment — there's nothing for a server
      // re-fetch to correct here. JS only needs to lift the CSS mask that
      // is_personalization_gated always applies (it can't distinguish "already
      // correct" from "blank-cart candidate needing a pick" at render time).
      resolveClientConditionalCandidates(currentSegments, tracker);

      const positioned = document.querySelectorAll('[data-personalization-container][data-position]');
      tracker.mark('no_cart_update_needed', { positionedCount: positioned.length });

      if (positioned.length > 0) {
        reorderSections(tracker);
      } else {
        revealPersonalizedContent(tracker, 'no-changes-no-reorder');
      }

      tracker.mark('pipeline_complete');
      return;
    }

    window.MB_FETCH_HIDDEN_ONLY = Boolean(
      userSegmentationChanged &&
      !hasExpiredSegments &&
      !hasInvalidatedSegments &&
      !campaignIdChanged &&
      !lpUrlChanged
    );
    const segmentChangeDirection = (hasExpiredSegments || hasInvalidatedSegments)
      ? (userSegmentationChanged ? 'mixed' : 'removals-only')
      : (userSegmentationChanged ? 'additions-only' : 'none');
    window.MB_SEGMENT_CHANGE_DIRECTION = segmentChangeDirection;
    tracker.mark('sections_fetch_scope_resolved', {
      hiddenOnly: window.MB_FETCH_HIDDEN_ONLY,
      userSegmentationChanged,
      hasExpiredSegments,
      hasInvalidatedSegments,
      campaignIdChanged,
      segmentChangeDirection
    });

    const updatedSegments = isSegmentSessionExpired()
    ? (window.MB_IS_LOGGED_IN
      ? currentSegments.filter(seg => isUserTypeSegment(seg))
      : [])
    : Array.from(new Set([...regexMatchSegments, ...retainedValidSegments]));

    resolveClientConditionalCandidates(updatedSegments, tracker);

    const cartAttributesPayload = {
      segment: updatedSegments.join(',')
    };

    if (resolvedCampaignId && campaignIdChanged) {
      cartAttributesPayload.current_campaign_id = resolvedCampaignId;
    }
    if (resolvedLpUrl && lpUrlChanged) {
      cartAttributesPayload.lp_url = resolvedLpUrl;
    }

    tracker.mark('cart_update_start', {
      attributes: cartAttributesPayload
    });

    try {
      let segmentSyncOk = false;

      const existingSegment = cart?.attributes?.segment || '';
      const existingCampaignId = cart?.attributes?.current_campaign_id || '';
      const existingLpUrl = cart?.attributes?.lp_url || '';
      const segmentAlreadySynced = areSegmentSetsEqual(cartAttributesPayload.segment, existingSegment);
      const campaignAlreadySynced = !Object.prototype.hasOwnProperty.call(cartAttributesPayload, 'current_campaign_id') ||
        cartAttributesPayload.current_campaign_id === existingCampaignId;
      const lpUrlAlreadySynced = !Object.prototype.hasOwnProperty.call(cartAttributesPayload, 'lp_url') ||
        cartAttributesPayload.lp_url === existingLpUrl;

      if (segmentAlreadySynced && campaignAlreadySynced && lpUrlAlreadySynced) {
        tracker.mark('cart_update_skipped_already_synced', {
          existingSegment,
          existingCampaignId,
          existingLpUrl
        });
        publishPersonalizationCartGuard(cartAttributesPayload);
        segmentSyncOk = true;
      }

      const cartUpdateResult = segmentSyncOk
        ? { ok: true }
        : await updateCartAttributesWithVerification(
          cartAttributesPayload,
          {
            expectedSegment: cartAttributesPayload.segment,
            verifyRetries: MB_CART_SYNC_VERIFY_RETRIES,
            verifyDelayMs: MB_CART_SYNC_VERIFY_DELAY_MS,
            tracker,
            trackerLabel: 'cart_update'
          }
        );

      if (!cartUpdateResult.ok) {
        if (cartUpdateResult.rateLimited) {
          tracker.mark('cart_update_rate_limited', {
            status: cartUpdateResult.status || 429,
            cooldownRemainingMs: cartUpdateResult.cooldownRemainingMs || 0
          });
          console.warn('Cart update rate-limited. Skipping immediate recovery attempt.');
          scheduleDeferredSegmentSyncRetry(cartAttributesPayload, tracker);
        } else {
          console.error('Cart update failed, retrying once:', cartUpdateResult.error);

          const cartUpdateRecoveryResult = await updateCartAttributesWithVerification(
            cartAttributesPayload,
            {
              expectedSegment: cartAttributesPayload.segment,
              verifyRetries: MB_CART_SYNC_VERIFY_RETRIES + 4,
              verifyDelayMs: MB_CART_SYNC_VERIFY_DELAY_MS,
              tracker,
              trackerLabel: 'cart_update_recovery'
            }
          );

          if (!cartUpdateRecoveryResult.ok) {
            console.error('Cart update recovery failed:', cartUpdateRecoveryResult.error);
          } else {
            publishPersonalizationCartGuard(cartAttributesPayload);
            segmentSyncOk = true;
          }
        }
      } else {
        publishPersonalizationCartGuard(cartAttributesPayload);
        segmentSyncOk = true;
        // Reconcile the analytics session mirror after a successful cart sync.
        // Uses the resolved campaign when present, otherwise the persisted cart
        // value; clears the mirror when neither is active. Preserves itc/lp_url.
        syncCampaignSessionMirror(
          resolvedCampaignId || (cart && cart.attributes && cart.attributes.current_campaign_id) || '',
          resolvedLpUrl || ''
        );
        tracker.mark('session_storage_synced');
      }

      if (!segmentSyncOk) {
        tracker.mark('segment_sync_not_persisted_skip_sections', {
          expectedSegment: cartAttributesPayload.segment
        });
        revealPersonalizedContent(tracker, 'segment-sync-failed');
        tracker.mark('pipeline_complete');
        return;
      }
    } catch (error) {
      console.error('Cart update error:', error);
      tracker.mark('cart_update_error', { message: String(error) });
    }

    await updatePersonalizedSections(tracker);

    const gpPostSwapReport = processGlobalPersonalization(updatedSegments);
    tracker.mark('global_personalization_executed', {
      source: 'post-swap',
      segmentCount: updatedSegments.length,
      segments: updatedSegments,
      report: gpPostSwapReport
    });

    schedulePostPipelineSegmentDriftChecks(cartAttributesPayload.segment, tracker);

    tracker.mark('pipeline_complete');
  } catch (error) {
    console.warn('Personalization error:', error);
    tracker.mark('pipeline_error', { message: String(error) });
    revealPersonalizedContent(tracker, 'pipeline-error');
  } finally {
    finalizeRunSummary('pipeline_finally');
    appendDebugBuffer('pipeline_finally', {
      hasResolver: Boolean(mbPersonalizationCartSyncResolver)
    });
    logDebugBufferSnapshot();

    if (mbPersonalizationCartSyncResolver) {
      mbPersonalizationCartSyncResolver();
      mbPersonalizationCartSyncResolver = null;
      window.MB_PERSONALIZATION_CART_SYNC_RESOLVE = null;
    }

    if (fallbackRevealTimer) {
      clearTimeout(fallbackRevealTimer);
    }
  }
})();